import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import {connect} from 'react-redux';
import {reduxForm, stopSubmit, formValueSelector} from 'redux-form';
import classNames from 'classnames';

import BookingInformation from 'components/BookingInformation';
import ErrorBlock from 'components/ErrorBlock';

import {formatDate} from 'utils/datetime';

import {clearField} from 'utils/forms';
import {
  MODALS,
  SCHEDULE_ITEM_TYPES,
  FETCH_SCHEDULE_DELAY,
  getNextFreeIdCounter,
  fetchXeroAccounts,
  fetchXeroTaxRates,
  createCreditNote,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';
import {selectModal} from 'ducks/modals';
import {delayedAction, composeActions} from 'ducks/common';

import {prepareFormData} from './utils';
import {BaseFields, ItemFields} from './components';

import './styles.css';

const CREATE_CREDIT_NOTE_FOR_NON_SYNCED_INVOICE_MESSAGE = `The invoice related to this credit note does not have a corresponding Xero invoice ID.
If you want the credit note to be synced please add a Xero ID via the invoice options before proceeding.
Click cancel to stop the operation.`;

const formSubmit = (values, dispatch, props) => {
  let shouldCreate = true;

  if (props.invoice && _.isNil(props.invoice.xeroId)) {
    shouldCreate = window.confirm(
      CREATE_CREDIT_NOTE_FOR_NON_SYNCED_INVOICE_MESSAGE
    );
  }

  if (!shouldCreate) {
    dispatch(stopSubmit('createCreditNote'));
    return;
  }

  const scheduleType = props.scheduleType || props.selectedScheduleType;

  const postData = prepareFormData(
    values,
    props.locationRef,
    _.get(props, 'invoice.id', null),
    props.tenancyId,
    scheduleType
  );

  const extraActions = props.getExtraActions({scheduleType});
  const delayedExtraActions = props.delayExtraActions({
    tenancyId: props.tenancyId,
    scheduleType
  });

  if (!_.isEmpty(delayedExtraActions)) {
    const delayedFetchSchedule = () =>
      delayedAction({
        delay: FETCH_SCHEDULE_DELAY,
        action: () => composeActions(delayedExtraActions)
      });
    extraActions.push(delayedFetchSchedule);
  }

  dispatch(createCreditNote(postData, extraActions));
};

const xeroAccountsMapping = xeroAccount => ({
  value: `${xeroAccount.id} - ${xeroAccount.label}`,
  label: xeroAccount.label,
  id: xeroAccount.id,
  accountClass: xeroAccount.accountClass
});

class CreditNoteCreateForm extends React.Component<any> {
  componentDidMount() {
    const {tenancyId, locationRef} = this.props;
    const getNextFreeIdCounterExtraAction = this.props.storeDataExtraActions(
      ({idCounter}) => ({idCounter})
    );

    this.props.getNextFreeIdCounter(
      {
        tenancyId,
        objectType: SCHEDULE_ITEM_TYPES.CREDIT_NOTE
      },
      getNextFreeIdCounterExtraAction
    );

    const fetchXeroAccountsExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroAccounts: data
      })
    );
    this.props.fetchXeroAccounts(
      {location: locationRef},
      fetchXeroAccountsExtraAction
    );

    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreateCreditNoteForPastMonths: _.get(data, 'canCreateFinanceItem')
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: this.props.locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }

  componentDidUpdate(prevProps) {
    const accountsFetched =
      _.get(this.props, 'xeroAccountOptions') !==
      _.get(prevProps, 'xeroAccountOptions');

    if (
      accountsFetched &&
      !_.isEmpty(_.get(this.props, 'xeroAccountOptions')) &&
      _.isEmpty(_.get(prevProps, 'xeroAccountOptions'))
    ) {
      const accountClass = _.get(
        this.props.xeroAccountOptions,
        '[0].accountClass'
      );
      this.fetchXeroTaxRates(accountClass);
    }
  }

  fetchXeroTaxRates = accountClass => {
    const fetchXeroTaxRatesExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroTaxRates: data
      })
    );
    const {locationRef} = this.props;

    this.props.fetchXeroTaxRates(
      {
        filters: {
          accountClass: accountClass,
          location: locationRef
        }
      },
      fetchXeroTaxRatesExtraAction
    );
    this.clearTaxRateValue();
  };

  clearTaxRateValue = () => {
    clearField(this, {field: 'taxRate'});
  };

  render() {
    const {
      className,
      handleSubmit,
      error,
      openFromBookingSet,
      booking,
      canCreateCreditNoteForPastMonths,
      scheduleType,
      selectedScheduleType
    } = this.props;
    return (
      <div>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form
          onSubmit={handleSubmit}
          className={classNames(className, 'CreditNoteCreateForm')}>
          {openFromBookingSet && <BookingInformation booking={booking} />}

          <BaseFields
            xeroAccounts={this.props.xeroAccountOptions}
            fetchXeroTaxRates={this.fetchXeroTaxRates}
            openFromBookingSet={openFromBookingSet}
            canCreateCreditNoteForPastMonths={canCreateCreditNoteForPastMonths}
            scheduleType={scheduleType || selectedScheduleType}
          />
          <ItemFields
            lineAmount={this.props.lineAmount}
            xeroTaxRates={this.props.xeroTaxRates || []}
          />
        </form>
      </div>
    );
  }
}

const formSelector = formValueSelector('creditNoteCreate');

const mapStateToProps = state => {
  const {
    data: {
      idCounter,
      xeroAccounts = [],
      xeroTaxRates,
      canCreateCreditNoteForPastMonths
    }
  } = selectModal(state, MODALS.CREDIT_NOTE_CREATE);

  const xeroAccountOptions = xeroAccounts.map(xeroAccountsMapping);

  let taxRate: any = null;
  if (!_.isEmpty(xeroTaxRates)) {
    taxRate = _.first(xeroTaxRates);
    taxRate = {
      value: taxRate.id,
      label: taxRate.name,
      effectiveRate: taxRate.effectiveRate
    };
  }

  const selectedAccount = formSelector(state, 'account');

  return {
    xeroAccountOptions,
    xeroTaxRates,
    initialValues: {
      idCounter,
      discount: '0',
      quantity: '1',
      dateIssued: formatDate(moment()),
      account: selectedAccount || _.first(xeroAccountOptions),
      taxRate
    },
    selectedScheduleType: _.get(formSelector(state, 'scheduleType'), 'value'),
    canCreateCreditNoteForPastMonths
  };
};

const mapDispatchToProps = {
  getNextFreeIdCounter,
  fetchXeroAccounts,
  fetchXeroTaxRates,
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'creditNoteCreate',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(CreditNoteCreateForm)
);
